import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Loader2, LogOut, Package, Pencil, Plus, Shirt, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, inr } from "@/lib/api";

const ALL_AGES = ["0-3M", "3-6M", "6-9M", "9-12M", "12-18M", "18-24M", "1-2Y", "2-3Y", "3-4Y", "4-5Y"];
const GENDERS = ["Girl", "Boy"];
const CATEGORIES = ["Baby Edit", "Festive Anarkalis", "Everyday Cotton", "Twirl Frocks", "Boys' Kurtas & Shirts", "Party Edit"];
const STATUSES = ["Awaiting Payment", "Placed", "Packed", "Shipped", "Delivered", "Cancelled"];

const emptyProduct = {
  name: "", description: "", category: CATEGORIES[0], gender: "Girl", price: "", mrp: "",
  fabric: "100% Organic Cotton", ages: [], sizes: [], colors: "", images: "", stock: 20, featured: false, badge: "",
};

function ProductForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial.id);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
        setForm((prev) => ({ ...prev, images: prev.images ? `${prev.images}\n${url}` : url }));
      }
      toast.success(files.length > 1 ? `${files.length} photos uploaded and linked` : "Photo uploaded and linked");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const [newImageUrl, setNewImageUrl] = useState("");
  const imageList = form.images.split("\n").map((u) => u.trim()).filter(Boolean);
  const setImages = (list) => setForm({ ...form, images: list.join("\n") });
  const addImageUrl = () => {
    const url = newImageUrl.trim();
    if (!url) return;
    setImages([...imageList, url]);
    setNewImageUrl("");
  };
  const setMainImage = (idx) => {
    const list = [...imageList];
    const [main] = list.splice(idx, 1);
    setImages([main, ...list]);
  };
  const removeImage = (idx) => setImages(imageList.filter((_, i) => i !== idx));
  const inputCls = "w-full rounded-xl border border-ink/10 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-terracotta";

  const toggleAge = (age) => {
    const ages = form.ages.includes(age) ? form.ages.filter((a) => a !== age) : [...form.ages, age];
    setForm({ ...form, ages, sizes: ages });
  };

  const save = async () => {
    if (!form.name.trim() || !form.price) {
      toast.error("Name and price are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseInt(form.price, 10),
      mrp: form.mrp ? parseInt(form.mrp, 10) : null,
      stock: parseInt(form.stock, 10) || 0,
      colors: form.colors.split(",").map((c) => c.trim()).filter(Boolean),
      images: form.images.split("\n").map((u) => u.trim()).filter(Boolean),
      badge: form.badge || null,
    };
    try {
      if (isEdit) await api.put(`/admin/products/${form.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(isEdit ? "Dress updated" : "Dress added to the collection");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-blush/60 rounded-2xl p-5 space-y-3" data-testid="admin-product-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <input className={inputCls} placeholder="Dress name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-form-name" />
        <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-form-category">
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select className={inputCls} value={form.gender || "Girl"} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="admin-form-gender">
          {GENDERS.map((g) => <option key={g}>{g}</option>)}
        </select>
      </div>
      <textarea className={`${inputCls} min-h-20 resize-none`} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-form-description" />
      <div className="grid grid-cols-3 gap-3">
        <input className={inputCls} placeholder="Price ₹ *" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="admin-form-price" />
        <input className={inputCls} placeholder="MRP ₹" type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} data-testid="admin-form-mrp" />
        <input className={inputCls} placeholder="Stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="admin-form-stock" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className={inputCls} placeholder="Fabric (e.g. Mulmul Cotton)" value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} data-testid="admin-form-fabric" />
        <input className={inputCls} placeholder="Colours, comma separated" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} data-testid="admin-form-colors" />
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-widest uppercase text-charcoal/70 mb-2">Age sizes (selected = available)</p>
        <div className="flex flex-wrap gap-2">
          {ALL_AGES.map((a) => (
            <button
              key={a}
              onClick={() => toggleAge(a)}
              className={`rounded-full px-3.5 py-1.5 font-mono text-xs border transition-colors ${form.ages.includes(a) ? "bg-ink text-cream border-ink" : "border-ink/15"}`}
              data-testid={`admin-form-age-${a.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-widest uppercase text-charcoal/70 mb-2">Photos — starred one is the main photo</p>
        {imageList.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-3" data-testid="admin-form-photo-list">
            {imageList.map((url, idx) => (
              <div key={`${url}-${idx}`} className={`relative rounded-xl p-1 bg-white border-2 ${idx === 0 ? "border-terracotta" : "border-ink/10"}`}>
                <img src={url} alt={`Product photo ${idx + 1}`} className="w-16 h-20 object-cover rounded-lg" />
                {idx === 0 ? (
                  <span className="absolute -top-2 -left-2 bg-terracotta text-white font-mono text-[8px] tracking-wider px-1.5 py-0.5 rounded-full">MAIN</span>
                ) : (
                  <button type="button" onClick={() => setMainImage(idx)} title="Set as main photo" className="absolute -top-2 -left-2 bg-white border border-ink/15 rounded-full p-1 hover:border-gold hover:text-gold" data-testid={`admin-photo-main-${idx}`} aria-label="Set as main photo">
                    <Star className="w-3 h-3" />
                  </button>
                )}
                <button type="button" onClick={() => removeImage(idx)} title="Remove photo" className="absolute -top-2 -right-2 bg-white border border-ink/15 rounded-full p-1 hover:border-red-400 hover:text-red-500" data-testid={`admin-photo-remove-${idx}`} aria-label="Remove photo">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input className={`${inputCls} flex-1 font-mono text-xs`} placeholder="Paste an image URL" value={newImageUrl} onChange={(e) => setNewImageUrl(e.target.value)} data-testid="admin-form-image-url" />
          <button type="button" onClick={addImageUrl} className="rounded-full border border-ink/15 px-4 text-xs font-semibold hover:border-terracotta hover:text-terracotta" data-testid="admin-form-add-url">Add</button>
        </div>
        <div className="flex items-center gap-3 mt-2.5">
          <label className={`cursor-pointer rounded-full border-2 border-dashed border-ink/20 px-5 py-2.5 text-xs font-semibold text-charcoal hover:border-terracotta hover:text-terracotta transition-colors ${uploading ? "opacity-50" : ""}`} data-testid="admin-form-upload-label">
            {uploading ? "Uploading photos..." : "Upload photos from device"}
            <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadPhoto} disabled={uploading} data-testid="admin-form-upload-input" />
          </label>
          <span className="font-mono text-[10px] text-charcoal/50">JPG/PNG/WEBP, 8MB each — pick several at once</span>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 items-center">
        <select className={inputCls} value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} data-testid="admin-form-badge">
          <option value="">No badge</option>
          {["Dress of the Season", "Trending", "Limited", "Bestseller", "New", "Festive Pick"].map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-charcoal">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="admin-form-featured" />
          Featured in collection
        </label>
      </div>
      <div className="flex gap-3 pt-1">
        <button onClick={save} disabled={saving} className="rounded-full bg-terracotta text-white px-6 py-2.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors disabled:opacity-50" data-testid="admin-form-save">
          {saving ? "Saving..." : isEdit ? "Save changes" : "Add dress"}
        </button>
        <button onClick={onCancel} className="rounded-full border border-ink/15 px-6 py-2.5 text-sm font-semibold" data-testid="admin-form-cancel">Cancel</button>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [auth, setAuth] = useState(undefined);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState("dresses");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setAuth(r.data)).catch(() => setAuth(null));
  }, []);

  const loadProducts = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data.products)).catch(() => {});
  }, []);
  const loadOrders = useCallback(() => {
    api.get("/admin/orders").then((r) => setOrders(r.data.orders)).catch(() => {});
  }, []);

  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (auth) {
      loadProducts();
      loadOrders();
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }
  }, [auth, loadProducts, loadOrders]);

  const doLogin = async () => {
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem("lj_admin_token", data.access_token);
      setAuth(data);
      toast.success(`Welcome back, ${data.name}`);
    } catch (e) {
      setLoginError(formatApiError(e));
    }
  };

  const doLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("lj_admin_token");
    setAuth(null);
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Remove this dress from the collection?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Dress removed");
      loadProducts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      toast.success(`Order ${id} → ${status}`);
      loadOrders();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `baeboos-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Orders CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const inputCls = "w-full rounded-2xl border border-ink/10 bg-white px-4 py-3.5 text-sm outline-none focus:border-terracotta";

  if (auth === undefined) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6" data-testid="admin-login-page">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm bg-white rounded-[2rem] p-8 shadow-xl">
          <p className="font-serif font-black text-3xl text-ink">Store Admin</p>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-sage-dark mt-2 mb-6">Baeboo's Wardrobe · Authorised only</p>
          <div className="space-y-3">
            <input className={inputCls} type="email" placeholder="Admin email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} data-testid="admin-login-email" />
            <input className={inputCls} type="password" placeholder="Password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && doLogin()} data-testid="admin-login-password" />
            {loginError && <p className="text-sm text-red-600" data-testid="admin-login-error">{loginError}</p>}
            <button onClick={doLogin} className="w-full rounded-full bg-ink text-cream py-3.5 text-sm font-semibold hover:bg-terracotta transition-colors" data-testid="admin-login-submit">
              Sign in
            </button>
            <Link to="/" className="block text-center font-mono text-[10px] tracking-widest uppercase text-charcoal/60 hover:text-terracotta" data-testid="admin-back-to-store">
              ← Back to store
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-ink text-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="font-serif font-bold text-xl">Baeboo's</span>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/60">Wardrobe Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-mono text-[10px] tracking-widest uppercase text-cream/70 hover:text-terracotta" data-testid="admin-view-store">View store</Link>
            <button onClick={doLogout} className="flex items-center gap-1.5 text-sm text-cream/80 hover:text-terracotta" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex gap-2 mb-8">
          {[["dresses", Shirt, `Dresses (${products.length})`], ["orders", Package, `Orders (${orders.length})`]].map(([key, Icon, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === key ? "bg-ink text-cream" : "bg-white text-charcoal border border-ink/10"}`}
              data-testid={`admin-tab-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8" data-testid="admin-stats-row">
            <div className="bg-white rounded-2xl p-4 sm:p-5" data-testid="admin-stat-today-orders">
              <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-charcoal/60">Today's orders</p>
              <p className="font-serif font-black text-2xl sm:text-3xl text-ink mt-1">{stats.today_orders}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 sm:p-5" data-testid="admin-stat-today-revenue">
              <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-charcoal/60">Today's revenue</p>
              <p className="font-serif font-black text-2xl sm:text-3xl text-terracotta mt-1">{inr(stats.today_revenue)}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 sm:p-5" data-testid="admin-stat-best-seller">
              <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-charcoal/60">Best-selling dress</p>
              <p className="font-serif font-bold text-base sm:text-xl text-ink mt-1 leading-snug">
                {stats.best_seller ? `${stats.best_seller.name} · x${stats.best_seller.qty}` : "No sales yet"}
              </p>
            </div>
          </div>
        )}

        {tab === "dresses" && (
          <div className="space-y-4">
            {!showAdd ? (
              <button onClick={() => { setShowAdd(true); setEditing(null); }} className="flex items-center gap-2 rounded-full bg-terracotta text-white px-6 py-3 text-sm font-semibold hover:bg-terracotta-dark transition-colors" data-testid="admin-add-product-btn">
                <Plus className="w-4 h-4" /> Add a new dress
              </button>
            ) : (
              <ProductForm initial={emptyProduct} onSaved={() => { setShowAdd(false); loadProducts(); }} onCancel={() => setShowAdd(false)} />
            )}
            {products.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl p-4" data-testid={`admin-product-row-${p.id}`}>
                {editing === p.id ? (
                  <ProductForm
                    initial={{ ...p, mrp: p.mrp || "", colors: p.colors.join(", "), images: p.images.join("\n"), badge: p.badge || "" }}
                    onSaved={() => { setEditing(null); loadProducts(); }}
                    onCancel={() => setEditing(null)}
                  />
                ) : (
                  <div className="flex items-center gap-4">
                    <img src={p.images[0]} alt={p.name} className="w-14 h-16 object-cover rounded-xl" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-ink truncate">{p.name}</p>
                      <p className="font-mono text-[10px] text-charcoal/60">{p.category} · stock {p.stock} · {p.sizes.join(" ")}</p>
                    </div>
                    <span className="font-serif font-bold text-terracotta">{inr(p.price)}</span>
                    <button onClick={() => { setEditing(p.id); setShowAdd(false); }} className="rounded-full border border-ink/10 p-2 hover:border-terracotta" data-testid={`admin-edit-${p.id}`} aria-label="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteProduct(p.id)} className="rounded-full border border-ink/10 p-2 hover:border-red-400 hover:text-red-500" data-testid={`admin-delete-${p.id}`} aria-label="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-4" data-testid="admin-orders-list">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-charcoal/60">All customer orders</p>
              <button
                onClick={exportCsv}
                className="flex items-center gap-2 rounded-full bg-ink text-cream px-5 py-2.5 text-sm font-semibold hover:bg-terracotta transition-colors"
                data-testid="admin-export-csv-btn"
              >
                <Download className="w-4 h-4" /> Download CSV
              </button>
            </div>
            {orders.length === 0 && <p className="text-charcoal font-serif italic text-lg">No orders yet — they'll appear here the moment a parent checks out.</p>}
            {orders.map((o) => (
              <div key={o.id} className="bg-white rounded-2xl p-5" data-testid={`admin-order-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-mono font-bold text-ink">{o.id}</p>
                    <p className="text-xs text-charcoal mt-0.5">{o.name} · {o.phone} · {o.city}, {o.state} {o.pincode}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-serif font-bold text-terracotta">{inr(o.total)}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest bg-peach text-terracotta-dark rounded-full px-3 py-1">{o.payment_method}</span>
                    {o.payment_status && (
                      <span
                        className={`font-mono text-[10px] uppercase tracking-widest rounded-full px-3 py-1 ${o.payment_status === "success" ? "bg-mint text-sage-dark" : o.payment_status === "failed" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}
                        data-testid={`admin-payment-status-${o.id}`}
                      >
                        {o.payment_status}
                      </span>
                    )}
                    <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="rounded-full border border-ink/10 px-3 py-1.5 text-xs font-semibold cursor-pointer" data-testid={`admin-order-status-${o.id}`}>
                      {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {o.items.map((i) => (
                    <span key={i.product_id + i.size} className="text-xs bg-cream rounded-full px-3 py-1.5 text-charcoal">
                      {i.name} · {i.size} · x{i.qty}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
